import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { gradeApi, type SectionPerformance } from '../../../lib/api/grade.api';
import { errorText } from '../../../lib/error-text';
import { useTenantStore } from '../../../stores/tenant.store';

// ─── Cómo rindió el curso ────────────────────────────────────────────────────
//
// OTRA PREGUNTA QUE EL RESTO DEL RESUMEN. Arriba se responde «qué hago ahora»;
// acá, «cómo fue» — que no se acciona, se lee al cerrar o al decidir qué
// cambiar el año próximo. Por eso va abajo mientras el curso corre.
//
// SIN LIBRERÍA DE GRÁFICOS, a propósito. Un histograma de cuatro tramos y una
// lista de barras por evaluación son cajas con un ancho porcentual; el repo no
// tiene ninguna librería de gráficos y agregarla para esto pesaría más que lo
// que dibuja. El día que haga falta dispersión o series de verdad, se agrega y
// se carga sólo acá.
//
// NADA SE PRECALCULA. Un dato materializado al cerrar el curso se desactualiza
// en silencio cuando alguien corrige una nota, y corregir una nota es algo que
// pasa a diario.

export function CoursePerformance({ sectionId }: { sectionId: string }): JSX.Element | null {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [datos, setDatos] = useState<SectionPerformance | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentTenantId || !sectionId) return;
    let vigente = true;
    setCargando(true);
    setError(null);
    gradeApi
      .getSectionPerformance({ tenantId: currentTenantId, sectionId })
      .then((d) => {
        if (vigente) setDatos(d);
      })
      .catch((e) => {
        if (vigente) setError(errorText(e));
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });
    return () => {
      vigente = false;
    };
  }, [currentTenantId, sectionId]);

  if (cargando) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={24} />
      </Stack>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  // NADA VENCIDO, NADA QUE CONTAR. Un curso recién empezado no «rindió» de
  // ninguna manera todavía, y dibujar ceros sería una acusación del calendario.
  if (!datos || datos.assignments.length === 0) return null;

  const totalEntregas = datos.onTime + datos.lateTotal;
  const maxTramo = Math.max(...datos.distribution.map((d) => d.count), 1);

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1" fontWeight={700}>
        {t('coursePerformance.title')}
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {/* ── DISTRIBUCIÓN ── el promedio esconde si hay dos grupos ── */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('coursePerformance.distribution')}
          </Typography>
          <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ height: 120, mt: 1 }}>
            {datos.distribution.map((d) => (
              <Stack key={`${d.from}-${d.to}`} alignItems="center" sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {d.count}
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    // Altura proporcional al tramo más alto. Un mínimo visible
                    // para que un tramo en cero no parezca ausente.
                    height: `${Math.max((d.count / maxTramo) * 88, 2)}px`,
                    bgcolor: d.to <= datos.passingGrade ? 'error.main' : 'success.main',
                    opacity: d.count === 0 ? 0.25 : 0.85,
                    borderRadius: 1,
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {Math.round(d.from)}–{Math.round(d.to)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Paper>

        {/* ── PUNTUALIDAD ── es una señal del DISEÑO del curso, no de notas ── */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('coursePerformance.punctuality')}
          </Typography>
          <Stack spacing={1} mt={1}>
            <Barra
              etiqueta={t('coursePerformance.onTime')}
              valor={datos.onTime}
              total={totalEntregas + datos.neverDelivered}
              color="success.main"
            />
            <Barra
              etiqueta={t('coursePerformance.late')}
              valor={datos.lateTotal}
              total={totalEntregas + datos.neverDelivered}
              color="warning.main"
            />
            <Barra
              etiqueta={t('coursePerformance.never')}
              valor={datos.neverDelivered}
              total={totalEntregas + datos.neverDelivered}
              color="error.main"
            />
          </Stack>
        </Paper>
      </Stack>

      {/* ── CÓMO RINDIÓ CADA EVALUACIÓN ── lo que cambia el curso el año que viene ──
       *
       * ESTO ERA UNA MURALLA DE FICHAS. Cada fila listaba con nombre a quien no
       * entregó Y a quien reprobó, y desde que el cero masivo existe son casi la
       * MISMA gente: en Teología II el Ensayo 1 mostraba diez reprobados y los
       * diez eran los mismos diez de «no entregaron», con un 0,0 al lado. Ocho
       * evaluaciones × ~16 fichas = ciento treinta fichas para no decir nada.
       *
       * QUIÉN NO ENTREGÓ NO VA ACÁ. Está completo, ordenado y sin repetir en
       * «Quiénes deben trabajo», que es el cuadro de abajo. Acá queda el número
       * —y los nombres en el globo, que no cuestan pantalla—, porque la pregunta
       * de este cuadro es cuál evaluación salió mal, no quién debe qué.
       *
       * LO QUE SÍ MERECE NOMBRE es quien ENTREGÓ y no alcanzó: ésos necesitan
       * ayuda con la materia, no un llamado telefónico. Son dos o tres por
       * evaluación, y antes quedaban tapados —la lista se corta en diez y los
       * ceros, al ordenar por nota, se quedaban con todos los lugares. */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {t('coursePerformance.byAssignment')}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
          {t('coursePerformance.averageHint')}
        </Typography>

        {/* Cabecera de columnas: sólo cuando hay ancho para ellas. */}
        <Box
          sx={{
            display: { xs: 'none', md: 'grid' },
            gridTemplateColumns: COLUMNAS,
            gap: 1,
            pb: 0.5,
            mb: 0.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Etiqueta>{t('coursePerformance.colAssignment')}</Etiqueta>
          <Etiqueta>{t('coursePerformance.colDelivered')}</Etiqueta>
          <Etiqueta align="right">{t('coursePerformance.colAverage')}</Etiqueta>
          <Etiqueta align="right">{t('coursePerformance.colAverageDelivered')}</Etiqueta>
        </Box>

        <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
          {datos.assignments.map((a) => {
            const sinEntregar = a.expected - a.delivered;
            const nombres = a.missingStudents.map((s) => s.studentName).join(', ');
            const ocultos = sinEntregar - a.missingStudents.length;
            return (
              <Box key={a.assignmentId} sx={{ py: 1 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr auto', md: COLUMNAS },
                    gap: 1,
                    alignItems: 'baseline',
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {a.name}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}
                  >
                    <Tooltip
                      title={
                        sinEntregar > 0
                          ? `${t('coursePerformance.missingTip', { names: nombres })}${
                              ocultos > 0
                                ? ` ${t('coursePerformance.andMore', { count: ocultos })}`
                                : ''
                            }`
                          : ''
                      }
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ cursor: sinEntregar > 0 ? 'help' : 'default' }}
                      >
                        {t('coursePerformance.deliveredOf', {
                          count: a.delivered,
                          total: a.expected,
                        })}
                      </Typography>
                    </Tooltip>
                    {a.late > 0 && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="warning"
                        label={t('coursePerformance.lateChip', { count: a.late })}
                        sx={{ height: 20 }}
                      />
                    )}
                  </Stack>

                  <Nota
                    valor={a.average}
                    passing={datos.passingGrade}
                    etiqueta={t('coursePerformance.colAverage')}
                  />
                  <Nota
                    valor={a.averageDelivered}
                    passing={datos.passingGrade}
                    etiqueta={t('coursePerformance.colAverageDelivered')}
                  />
                </Box>

                {/* Sólo quien entregó y no alcanzó. Con nombre, que es lo accionable. */}
                {a.failedStudents.length > 0 && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 0.75 }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {t('coursePerformance.failedDelivered')}
                    </Typography>
                    {a.failedStudents.map((s) => (
                      <Tooltip key={`f-${s.studentId}`} title={t('coursePerformance.failedTip')}>
                        <Chip
                          size="small"
                          variant="outlined"
                          color="warning"
                          label={`${s.studentName} · ${s.grade?.toFixed(1)}`}
                          sx={{ height: 20 }}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                )}
                {a.failedStudents.length === 0 && a.delivered > 0 && (
                  <Typography
                    variant="caption"
                    color="success.main"
                    sx={{ mt: 0.75 }}
                    display="block"
                  >
                    {t('coursePerformance.allPassed')}
                  </Typography>
                )}
                {a.delivered === 0 && (
                  <Typography
                    variant="caption"
                    color="error.main"
                    sx={{ mt: 0.75 }}
                    display="block"
                  >
                    {t('coursePerformance.noneDelivered')}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </Paper>
    </Stack>
  );
}

/** Cuatro columnas: la evaluación se lleva el espacio, los números van fijos y
 *  alineados a la derecha para poder compararlos de un vistazo hacia abajo. */
const COLUMNAS = '1fr auto 90px 110px';

function Etiqueta({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}): JSX.Element {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ textAlign: align, letterSpacing: 0.4, textTransform: 'uppercase', fontSize: 10 }}
    >
      {children}
    </Typography>
  );
}

/** Una nota, alineada a la derecha y en rojo si no alcanza. `—` cuando no hay
 *  nada que promediar: un 0 ahí diría que les fue pésimo, y lo que pasó es que
 *  no hubo trabajo.
 *
 *  LLEVA SU PROPIA ETIQUETA EN EL TELÉFONO. La cabecera de columnas se esconde
 *  en pantalla angosta —no caben cuatro—, y sin ella los dos promedios quedaban
 *  uno al lado del otro, dos números pelados, sin nada que dijera cuál era el
 *  de acta y cuál el de quienes entregaron. Justo la distinción que este cambio
 *  vino a hacer visible. */
function Nota({
  valor,
  passing,
  etiqueta,
}: {
  /** `undefined` cuando el backend todavía no manda el campo: Hosting se
   *  despliega antes que Backend y hay una ventana en que conviven. */
  valor: number | null | undefined;
  passing: number;
  etiqueta: string;
}): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 0.5,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: { xs: 'block', md: 'none' }, fontSize: 10 }}
      >
        {etiqueta}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={700}
        color={valor == null ? 'text.disabled' : valor < passing ? 'error.main' : 'success.main'}
        sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', flex: 1 }}
      >
        {valor == null ? '—' : valor.toFixed(1)}
      </Typography>
    </Box>
  );
}

function Barra({
  etiqueta,
  valor,
  total,
  color,
}: {
  etiqueta: string;
  valor: number;
  total: number;
  color: string;
}): JSX.Element {
  const pct = total > 0 ? (valor / total) * 100 : 0;
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="caption" color="text.secondary">
          {etiqueta}
        </Typography>
        <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {valor} · {Math.round(pct)}%
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{ height: 6, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: color } }}
      />
    </Box>
  );
}
